import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Briefcase, Clock, IndianRupee, Building2, CheckCircle2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { INPUT_LIGHT } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { FadeUp, SectionTag } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", resume_link: "", cover_note: "" });

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name, email: f.email || user.email }));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    if (f && f.size > 5 * 1024 * 1024) {
      toast.error("File too large — max 5 MB.");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let resumeFileId = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
        resumeFileId = up.data.id;
      }
      await api.post(`/jobs/${id}/apply`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        resume_link: form.resume_link || null,
        resume_file_id: resumeFileId,
        cover_note: form.cover_note || null,
      });
      setSubmitted(true);
      toast.success("Application submitted. A recruiter will reach out shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (notFound) {
    return (
      <main className="pt-48 pb-40 text-center" data-testid="job-not-found">
        <p className="font-editorial italic text-4xl text-slate-400">This role has moved on.</p>
        <Link to="/careers" data-testid="job-back-link" className="mt-6 inline-flex items-center gap-2 text-sm text-brand">
          <ArrowLeft size={15} /> Back to all roles
        </Link>
      </main>
    );
  }

  if (!job) {
    return <main className="pt-48 pb-40 max-w-7xl mx-auto px-6"><div className="h-64 rounded-2xl border border-slate-200 bg-white animate-pulse" data-testid="job-loading" /></main>;
  }

  const isOpen = job.status === "open";

  return (
    <main data-testid="job-detail-page" className="pt-40 pb-32">
      <Seo
        title={`${job.title} at ${job.company} — Samanvay HR Careers`}
        description={`${job.title} — ${job.company}, ${job.location}. ${job.type} role via Samanvay HR. Apply in two minutes; a human recruiter reads every application.`}
      />
      <div className="max-w-7xl mx-auto px-6">
        <Link to="/careers" data-testid="job-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand transition-colors duration-200">
          <ArrowLeft size={15} /> All roles
        </Link>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <FadeUp>
              <SectionTag>{job.industry}</SectionTag>
              <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-slate-900 leading-[1.02]" data-testid="job-title">
                {job.title}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
                <span className="flex items-center gap-2"><Building2 size={15} className="text-brand" /> {job.company}</span>
                <span className="flex items-center gap-2"><MapPin size={15} className="text-brand" /> {job.location}</span>
                <span className="flex items-center gap-2"><Briefcase size={15} className="text-brand" /> {job.type}</span>
                {job.experience && <span className="flex items-center gap-2"><Clock size={15} className="text-brand" /> {job.experience}</span>}
                {job.salary && <span className="flex items-center gap-2"><IndianRupee size={15} className="text-brand" /> {job.salary}</span>}
              </div>

              <div className="mt-12 border-t border-slate-200 pt-10">
                <h2 className="font-display text-xl font-semibold text-slate-900">The role</h2>
                <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-line" data-testid="job-description">{job.description}</p>
              </div>

              {job.skills?.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-display text-xl font-semibold text-slate-900">What you&rsquo;ll bring</h2>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="job-skills">
                    {job.skills.map((s) => (
                      <span key={s} className="rounded-full border border-slate-200/60 bg-slate-100 px-4 py-1.5 text-xs uppercase tracking-[0.15em] text-slate-500">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </FadeUp>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.15}>
              <div className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="apply-panel">
                {submitted ? (
                  <div className="text-center py-10" data-testid="apply-success">
                    <CheckCircle2 size={44} className="mx-auto text-brand" />
                    <h3 className="mt-6 font-display text-2xl font-semibold text-slate-900">Application in.</h3>
                    <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                      A recruiter reads every application within one business day. Keep an eye on your inbox.
                    </p>
                    <Link to="/careers" data-testid="apply-more-roles-btn" className="mt-8 inline-block rounded-full border border-slate-200 px-6 py-3 text-sm text-slate-600 hover:border-brand/60 hover:text-brand transition-colors duration-300">
                      Browse more roles
                    </Link>
                  </div>
                ) : !isOpen ? (
                  <div className="text-center py-10" data-testid="apply-closed">
                    <p className="font-editorial italic text-2xl text-slate-400">This role is no longer accepting applications.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
                    <h3 className="font-display text-2xl font-semibold text-slate-900">Apply for this role</h3>
                    <div className="space-y-2">
                      <Label htmlFor="apply-name" className="text-slate-500">Full name</Label>
                      <Input id="apply-name" required value={form.name} onChange={set("name")} data-testid="apply-name-input"
                        className={INPUT_LIGHT} placeholder="Asha Verma" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apply-email" className="text-slate-500">Email</Label>
                      <Input id="apply-email" type="email" required value={form.email} onChange={set("email")} data-testid="apply-email-input"
                        className={INPUT_LIGHT} placeholder="asha@email.com" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apply-phone" className="text-slate-500">Phone</Label>
                      <Input id="apply-phone" value={form.phone} onChange={set("phone")} data-testid="apply-phone-input"
                        className={INPUT_LIGHT} placeholder="+91 98XXX XXXXX" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-500">Resume (PDF, DOC — max 5 MB)</Label>
                      {file ? (
                        <div className="flex items-center justify-between rounded-lg border border-brand/40 bg-orange-50 px-4 py-3" data-testid="apply-file-chosen">
                          <span className="flex items-center gap-2 text-sm text-slate-700 truncate">
                            <Paperclip size={15} className="text-brand shrink-0" /> {file.name}
                          </span>
                          <button type="button" onClick={() => setFile(null)} data-testid="apply-file-remove-btn" className="text-slate-400 hover:text-slate-700 transition-colors duration-200">
                            <X size={15} />
                          </button>
                        </div>
                      ) : (
                        <label data-testid="apply-resume-file-label" className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3.5 text-sm text-slate-500 transition-colors duration-200 hover:border-brand/50 hover:text-slate-700">
                          <Paperclip size={16} className="text-brand" />
                          <span>Attach resume file</span>
                          <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="apply-resume-file-input" onChange={pickFile} />
                        </label>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apply-resume" className="text-slate-500">Or paste a resume / LinkedIn link</Label>
                      <Input id="apply-resume" type="url" value={form.resume_link} onChange={set("resume_link")} data-testid="apply-resume-input"
                        className={INPUT_LIGHT} placeholder="https://" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apply-note" className="text-slate-500">Why this role? (optional)</Label>
                      <Textarea id="apply-note" rows={4} value={form.cover_note} onChange={set("cover_note")} data-testid="apply-note-input"
                        className={INPUT_LIGHT} placeholder="Two sentences is plenty." />
                    </div>
                    <button
                      type="submit"
                      disabled={saving}
                      data-testid="apply-submit-btn"
                      className="w-full rounded-full bg-brand py-3.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow disabled:opacity-50"
                    >
                      {saving ? "Submitting..." : "Submit application"}
                    </button>
                  </form>
                )}
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
}
